import { eventFactory } from "OK/EventFactoryForEventBus";

export interface IEventBusParams {
    name: string;
    prefix: string;
}

export interface IEventbusData<TData> {
    prefix: string;
    name: string;
    data: TData;
    target: object | null;
}

export type IEventbusEvent<TData> = CustomEvent<IEventbusData<TData>>;

export interface IEventbusListener<TData> extends EventListener {
    (evt: IEventbusEvent<TData>): void;
}

type UListenerResult = boolean | void;

export type IListener<TData> = (data: TData) => UListenerResult | Promise<UListenerResult>;

export class EventBus<TData = undefined> {
    static EVENT_NAME = 'okeventbus';

    readonly prefix: string;
    readonly name: string;

    protected listeners = new Map<IListener<TData>, IEventbusListener<TData>>();
    static eventHost = eventFactory.create();

    static registeredBuses = new Set<EventBus<any>>();

    constructor(params: IEventBusParams) {
        this.prefix = params.prefix;
        this.name = params.name;
    }

    static getRegisteredBuses = () => {
        return EventBus.registeredBuses;
    }

    static getHost = () => {
        return EventBus.eventHost;
    }

    static createEvent = <TData>(data: IEventbusData<TData>): IEventbusEvent<TData> => {
        return new CustomEvent(EventBus.EVENT_NAME, {
            detail: data,
            bubbles: false,
            cancelable: true
        });
    }

    static on = <TData>(listener: IEventbusListener<TData>) => {
        if (EventBus.hasListener(listener)) {
            throw new Error('EventBus: повторное добавление обработчика');
        }

        EventBus.eventHost.attach(listener);
    };

    static off = <TData>(listener: IEventbusListener<TData>) => {
        EventBus.eventHost.detach(listener);
    };

    static emit = <TData>(data: IEventbusData<TData>): boolean => {
        const evt = EventBus.createEvent<TData>(data);

        EventBus.eventHost.trigger<TData>(evt);

        return !evt.defaultPrevented;
    };

    static findRegistered = <TData>(params: IEventBusParams): EventBus<TData> | null => {
        let bus: EventBus<TData> | null = null;

        EventBus.registeredBuses.forEach(function (item) {
            if (!bus && item.isMe(params)) {
                bus = item;
            }
        });

        return bus;
    };

    static create = <TData>(params: IEventBusParams): EventBus<TData> => {
        return EventBus.getInstance(params);
    };

    static getInstance = <TData = undefined>(params: IEventBusParams): EventBus<TData> => {
        let bus = EventBus.findRegistered<TData>(params);

        // Если кто-то уже регал такую, то ее и отдаем
        if (!bus) {
            bus = new EventBus<TData>(params);
            EventBus.getRegisteredBuses().add(bus);
        }

        return bus;
    }

    static hasListener = <TData>(listener: IEventbusListener<TData>) => {
        const allListeners = EventBus.eventHost.list;
        return allListeners.some((invocationItem) =>
            invocationItem.handler === listener
        );
    }

    emit = (data: TData | null, target: object | null = null): boolean => {
        return EventBus.emit({
            prefix: this.prefix,
            name: this.name,
            data: data,
            target: target
        });
    }

    on = (listener: IListener<TData>, target: object | null): boolean => {
        const newListener = this.makeListener(listener, target!);
        if (newListener) {
            EventBus.on(newListener);
            return true;
        }
        return false;
    }

    off = (listener: IListener<TData>) => {
        const realListener = this.listeners.get(listener);

        if (realListener) {
            EventBus.off(realListener);
            this.listeners.delete(listener);
        }
    }

    isMe = (params: IEventBusParams) => {
        return (
            this.name === params.name &&
            this.prefix === params.prefix
        );
    }

    makeListener = (listener: IListener<TData>, target: object) => {
        if (this.listeners.has(listener)) {
            window.OK.logging.info('EventBus[' + this.prefix + ', ' + this.name + ']: повторное добавление обработчика');
            return;
        }

        // чтобы исправить Error "'this' implicitly has type 'any'" when used .bind()
        // https://github.com/Microsoft/TypeScript/issues/19639
        const self = this;

        const newListener: IEventbusListener<TData> = (function (e: IEventbusEvent<TData>) {
            const detail = e.detail;

            if (e.defaultPrevented) {
                return;
            }


            if (!self.isMe(detail)) {
                return;
            }

            // Если инициатор события сам на него нарвался,
            // то стоит скипнуть, чтобы не словить сайд эфеект
            if (target && detail.target && target === detail.target) {
                return;
            }

            return listener(detail.data);

            // утверждение типа т.к.
            // https://github.com/microsoft/TypeScript/issues/28357#issuecomment-436484705
        } as EventListener).bind(this);

        this.listeners.set(listener, newListener);

        return newListener;
    }

    static factory(prefix: string): <T>(name: string) => EventBus<T>  {
        return <T>(name: string) => EventBus.create<T>({
            prefix,
            name
        });
    }
}
